import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatAuDate, formatAuDateTime } from "@/lib/date";
import { CheckCircle2, XCircle, Loader2, ArrowLeft, Share2, Mail, MessageCircle, Copy, Send } from "lucide-react";
import { toast } from "sonner";

const dayLabel = (iso) => formatAuDate(iso);

export default function Confirm() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/confirm/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  async function respond(answer) {
    setSubmitting(true);
    try {
      const { data: r } = await api.post(`/confirm/${token}`, { response: answer });
      setData((d) => ({ ...d, status: r.status, confirmed_at: r.confirmed_at }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…</div>;
  }
  if (notFound || !data) {
    // Show the token so the volunteer can quote it back if they email the organiser — makes
    // debugging a bad SMS URL trivial instead of guessing.
    const preview = (token || "").slice(0, 12);
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="border border-black/10 bg-white p-10 max-w-md text-center" data-testid="link-not-found">
          <XCircle className="mx-auto h-10 w-10 text-[#FF3B30]" />
          <h1 className="mt-4 font-display text-2xl font-black">Link not found</h1>
          <p className="mt-2 text-sm text-zinc-600">This confirmation link is invalid or has expired.</p>
          {preview ? (
            <p className="mt-3 text-[11px] font-mono text-zinc-400" data-testid="link-token-preview">
              Ref: {preview}…
            </p>
          ) : null}
          <Link to="/" className="mt-6 inline-block brand-btn px-4 py-2 text-sm">Home</Link>
        </div>
      </div>
    );
  }

  const finalized = data.status === "confirmed" || data.status === "declined";
  const slug = data.event?.slug || "";
  const registerLink = slug ? `${window.location.origin}/events/${slug}/register` : window.location.origin;

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <div className="mx-auto max-w-3xl px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-black">
          <ArrowLeft className="h-4 w-4" /> VOLMEE
        </Link>

        <div className="mt-6 border border-black/10 bg-white p-8 md:p-12 shadow-[8px_8px_0_0_rgba(0,0,0,0.08)]">
          <div className="text-xs uppercase tracking-widest text-[#002FA7]">Volunteer confirmation</div>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tighter md:text-5xl" data-testid="confirm-heading">
            Hi {data.name}.
          </h1>
          <p className="mt-3 text-sm text-zinc-600">Please confirm your allocation below.</p>

          {data.assignments && data.assignments.length > 0 ? (
            <div className="mt-6 space-y-3" data-testid="confirm-assignments">
              {data.event?.name ? <div className="font-mono text-sm"><b>Event:</b> {data.event.name}</div> : null}
              {data.assignments.map((a, i) => {
                const times = (a.start || a.end) ? `${a.start || "?"} – ${a.end || "?"}` : "";
                const loc = (a.location || "").trim();
                const mapsHref = loc ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc)}` : "";
                const rowKey = `${a.day}-${a.session}-${a.role}-${i}`;
                return (
                  <div key={rowKey} className="border border-black/10 bg-[#F4F4F5] p-5 font-mono text-sm" data-testid={`confirm-assignment-${i}`}>
                    <div><b>Date:</b> {dayLabel(a.day)}</div>
                    <div><b>Event element:</b> {a.session}</div>
                    {times ? <div data-testid={`confirm-assignment-time-${i}`}><b>Time:</b> {times}</div> : null}
                    {loc ? (
                      <div data-testid={`confirm-assignment-location-${i}`}>
                        <b>Location:</b>{" "}
                        <a href={mapsHref} target="_blank" rel="noreferrer" className="text-[#002FA7] underline decoration-dotted hover:decoration-solid" title="Open in Google Maps">📍 {loc}</a>
                      </div>
                    ) : null}
                    <div><b>Role:</b> {a.role}</div>
                  </div>
                );
              })}
              {data.assignments.length > 1 ? (
                <p className="text-xs italic text-zinc-500">You have {data.assignments.length} roles across this event. One click below confirms all of them.</p>
              ) : null}
            </div>
          ) : null}

          {!finalized ? (
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              <button
                onClick={() => respond("yes")}
                disabled={submitting}
                data-testid="confirm-yes-btn"
                className="brand-btn px-6 py-6 text-base font-black tracking-tight disabled:opacity-50"
              >
                YES I CAN DO THOSE ROLES
              </button>
              <button
                onClick={() => respond("no")}
                disabled={submitting}
                data-testid="confirm-no-btn"
                className="brand-btn-red px-6 py-6 text-base font-black tracking-tight disabled:opacity-50"
              >
                I CAN'T DO THOSE ROLES — PLEASE CONTACT ME
              </button>
            </div>
          ) : (
            <div className="mt-8">
              {data.status === "confirmed" ? (
                <>
                  <div className="border border-emerald-200 bg-emerald-50 p-6" data-testid="confirmed-state">
                    <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                    <h2 className="mt-3 font-display text-2xl font-black">Thank you — you're confirmed.</h2>
                    <p className="mt-2 text-sm text-emerald-900">Confirmed at {formatAuDateTime(data.confirmed_at)}</p>
                  </div>
                  <ShareBlock
                    eventName={data.event?.name || ""}
                    assignments={data.assignments || []}
                    registerLink={registerLink}
                    volunteerName={data.name}
                  />
                </>
              ) : (
                <div className="border border-red-200 bg-red-50 p-6" data-testid="declined-state">
                  <XCircle className="h-8 w-8 text-[#FF3B30]" />
                  <h2 className="mt-3 font-display text-2xl font-black">No problem — we'll be in touch.</h2>
                  <p className="mt-2 text-sm text-red-900">A team member will contact you shortly to find a better fit.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ShareBlock({ eventName, assignments, registerLink, volunteerName }) {
  const message = useMemo(() => {
    if (!assignments.length) return `I'm volunteering at ${eventName}. Want to join me? Sign up here: ${registerLink}`;
    const lines = assignments.slice(0, 5).map((a) => {
      const times = (a.start || a.end) ? ` (${a.start || "?"}–${a.end || "?"})` : "";
      return `• ${dayLabel(a.day)} — ${a.session}${times} — ${a.role}`;
    });
    const extra = assignments.length > 5 ? `\n…plus ${assignments.length - 5} more` : "";
    return `I've just signed up to volunteer at ${eventName}! Here are my roles:\n${lines.join("\n")}${extra}\n\nCome join me — sign up for similar timings here:\n${registerLink}`;
  }, [eventName, assignments, registerLink]);

  const subject = `Volunteer with me at ${eventName}`;

  async function doNativeShare() {
    if (navigator.share) {
      try {
        // Note: intentionally NOT passing a title — some platforms (like WhatsApp on Android)
        // insert the browser page title as a "shared from" prefix. Text-only keeps the message
        // looking like it came from the volunteer themselves.
        await navigator.share({ text: message });
      } catch (e) { /* user cancelled */ }
    } else {
      copy();
      toast.info("Share sheet not available — message copied to clipboard");
    }
  }
  function copy() {
    navigator.clipboard.writeText(message).then(() => toast.success("Share message copied to clipboard"));
  }
  const emailHref = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
  const smsHref = `sms:?&body=${encodeURIComponent(message)}`;
  const waHref = `https://wa.me/?text=${encodeURIComponent(message)}`;

  return (
    <div className="mt-6 border-2 border-[#002FA7] bg-white p-6" data-testid="share-block">
      <div className="flex flex-wrap items-center gap-3">
        <Share2 className="h-6 w-6 text-[#002FA7]" />
        <div className="flex-1">
          <div className="text-xs font-medium uppercase tracking-widest text-[#002FA7]">Bring a friend</div>
          <h3 className="mt-1 font-display text-xl font-black tracking-tighter">Share your itinerary and invite someone to volunteer alongside you.</h3>
          <p className="mt-1 text-xs text-zinc-600">We'll pre-fill the message with the day, event element, role and times you've signed up for so they can pick similar slots.</p>
        </div>
      </div>

      <textarea
        readOnly
        value={message}
        className="mt-4 w-full min-h-[120px] border border-black/10 bg-[#F9F9FB] px-3 py-2 text-xs font-mono text-zinc-800"
        data-testid="share-message-textarea"
      />

      <div className="mt-3 grid gap-2 sm:grid-cols-2 md:grid-cols-4">
        <button onClick={doNativeShare} data-testid="share-native-btn" className="inline-flex items-center justify-center gap-1.5 border border-black bg-black px-3 py-2 text-xs font-semibold text-white hover:bg-white hover:text-black">
          <Send className="h-3.5 w-3.5" /> Share…
        </button>
        <a href={smsHref} data-testid="share-sms-btn" className="inline-flex items-center justify-center gap-1.5 border border-black/20 bg-white px-3 py-2 text-xs font-semibold hover:bg-black hover:text-white">
          <MessageCircle className="h-3.5 w-3.5" /> Text (SMS)
        </a>
        <a href={emailHref} data-testid="share-email-btn" className="inline-flex items-center justify-center gap-1.5 border border-black/20 bg-white px-3 py-2 text-xs font-semibold hover:bg-black hover:text-white">
          <Mail className="h-3.5 w-3.5" /> Email
        </a>
        <a href={waHref} target="_blank" rel="noreferrer" data-testid="share-whatsapp-btn" className="inline-flex items-center justify-center gap-1.5 border border-black/20 bg-white px-3 py-2 text-xs font-semibold hover:bg-black hover:text-white">
          <Share2 className="h-3.5 w-3.5" /> WhatsApp
        </a>
      </div>
      <div className="mt-2 flex justify-end">
        <button onClick={copy} data-testid="share-copy-btn" className="inline-flex items-center gap-1 text-[11px] text-[#002FA7] underline">
          <Copy className="h-3 w-3" /> Copy to clipboard
        </button>
      </div>
    </div>
  );
}
